import CopyButton from "@components/buttons/CopyButton";
import RedirectButton from "@components/buttons/RedirectButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import TruncatedCopyValue from "@components/rowcomponents/TruncatedCopyValue";
import { primeServices } from "@services/apis/Prime";
import { transactionApiServices } from "@services/apis/Transactions";
import {
  GetPrimeCadTradeDetailsData,
  GetPrimeCadTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeCadTradeDetailsModel";
import {
  SettleTransactionRequest,
  SettleTransactionResponse,
} from "@services/models/Transactions/SettleTransactionModel";
import React, { useEffect, useState } from "react";
import webSocketService from "../../services/websocket/WebsocketServices";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import ConfirmCadExpirePanel from "./components/ConfirmCadExpirePanel";
import ConfirmRefundPanel from "./components/ConfirmRefundPanel";
import ConfirmReinitiatePanel from "./components/ConfirmReinitiatePanel";
import EventTimeline from "./components/EventTimeline";
import InvertibleDateTime from "./components/InvertibleDateTime";
import InvertibleRate from "./components/InvertibleRate";
import PrimeDetailsActionButton from "./components/PrimeDetailsActionButton";
import PrimeOrderFlowDetailsSkeleton from "./components/PrimeOrderFlowDetailsSkeleton";
import {
  getCanadaEventPillClass,
  getSettlementTypePill,
  getTransferTypePill,
  OrderDetailsPills,
} from "./components/PrimeOrderPills";
import {
  canCadExpire,
  canCadRefund,
  canCadReinitiate,
} from "./utils/cadIvyActionEligibility";

interface PrimeCanadaOrderFlowDetailsProps {
  instanceId: string;
  onEventChange?: (instanceId: string, newEventId: number) => void;
}

export const PrimeCanadaOrderFlowDetails: React.FC<
  PrimeCanadaOrderFlowDetailsProps
> = ({ instanceId, onEventChange }) => {
  const [data, setData] = useState<GetPrimeCadTradeDetailsData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [onClickStatus, setOnClickStatus] = useState<boolean>(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [reinitiateOpen, setReinitiateOpen] = useState(false);
  const [expireOpen, setExpireOpen] = useState(false);
  const [expireRequested, setExpireRequested] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetPrimeCadTradeDetailsResponse =
        await primeServices.getCadInstanceDetails({ instanceId });

      const processedData = {
        ...response.data,
      };

      setData(processedData);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const handleSendCallback = async () => {
    try {
      setIsButtonLoading(true);
      const settleTransactionRequest: SettleTransactionRequest = {
        instanceId: instanceId,
      };

      const response: SettleTransactionResponse =
        await transactionApiServices.settleTransaction(
          settleTransactionRequest,
          "/adminPanel/sendCADWebhook",
        );
      handleSuccess({
        message: response.msg || "Transaction settled successfully",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsButtonLoading(false);
    }
  };

  useEffect(() => {
    setExpireRequested(false);
    setExpireOpen(false);
    fetchData();
  }, [instanceId]);

  const handleTradeChangeWebSockets = (
    socketData: GetPrimeCadTradeDetailsData,
  ) => {
    if (socketData.instanceId === instanceId) {
      const processedData = {
        ...socketData,
      };
      setData(processedData);
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "cad_instance_details",
          true,
          handleTradeChangeWebSockets,
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected]);

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return <PrimeOrderFlowDetailsSkeleton width="w-full" />;
  }

  const tradeTypeLabel =
    data?.isBuying === 1 || data?.isBuying === true ? "BUY" : "SELL";

  const eventDetails = [...(data?.eventDetails ?? [])].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  const settlementDetails = data?.cryptoSettlementDetails;
  const transferPill = getTransferTypePill(settlementDetails?.transferType);
  const settlementPill = getSettlementTypePill(
    settlementDetails?.settlementType,
  );
  const settlementHash = settlementDetails?.transactionHash?.trim() || "";
  const settlementNetwork = settlementDetails?.network?.trim() || "";
  const actionContext = {
    transferType: settlementDetails?.transferType,
    isBuying: data?.isBuying,
    eventId: data?.eventId,
    eventDetails: data?.eventDetails ?? undefined,
  };
  const showRefund = canCadRefund(actionContext);
  const showReinitiate = canCadReinitiate(actionContext);
  const showExpire = canCadExpire(actionContext) && !expireRequested;

  const handleEventChange = (id: string, newEventId: number) => {
    setData((prev) => (prev ? { ...prev, eventId: newEventId } : prev));
    onEventChange?.(id, newEventId);
  };

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px]"
      style={{ height: "65dvh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Merchant Details:
          </p>
          <LabelRow
            label="Merchant ID | User ID"
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.merchantId}
                </span>
                <CopyButton
                  copyText={data?.merchantId?.toString() ?? ""}
                  copyMessage="Copied to clipboard"
                />
                <span className="text-text-base text-[12px] font-normal">
                  |
                </span>
                <span className="text-text-base text-[12px] font-normal">
                  {data?.userId}
                </span>
                <CopyButton
                  copyText={(data?.userId || "").toString()}
                  copyMessage="Copied to clipboard"
                />
              </div>
            }
          />
          <LabelRow label="Merchant Name" value={data?.merchantName} />
        </div>
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Sub Merchant Details:
        </p>

        <LabelRow
          label="Merchant Name"
          value={
            data?.merchantDetails?.merchantName ??
            data?.merchantDetails?.merchantName ??
            "- -"
          }
          isCopy={
            !!data?.merchantDetails?.merchantName ||
            !!data?.merchantDetails?.merchantName
          }
        />

        <LabelRow
          label="Merchant Domain"
          value={
            data?.merchantDetails?.domain ??
            data?.merchantDetails?.merchantUrl ??
            "- -"
          }
          isCopy={
            !!data?.merchantDetails?.domain ||
            !!data?.merchantDetails?.merchantUrl
          }
        />
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-navbar-active text-[12px] font-semibold underline">
              Order Details:
            </p>
            <OrderDetailsPills
              tradeType={tradeTypeLabel}
              eventId={data?.eventId}
              eventClassName={getCanadaEventPillClass(data?.eventId)}
            />
          </div>

          <LabelRow
            label="Merchant Ref ID"
            value={data?.merchantRefId ?? ""}
            valueComp={
              <TruncatedCopyValue
                value={data?.merchantRefId}
                copyMessage="Merchant Ref ID copied to clipboard"
              />
            }
          />
          <LabelRow
            label="Instance ID"
            value={data?.instanceId ?? ""}
            isCopy={!!data?.instanceId}
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.instanceId && data?.instanceId.substring(0, 8)}...
                </span>
                <CopyButton
                  copyText={data?.instanceId ?? ""}
                  copyMessage="Copied to clipboard"
                />
              </div>
            }
          />
          <LabelRow
            label="Bank Reference"
            value={data?.interacReferenceNumber ?? "- -"}
            isCopy={!!data?.interacReferenceNumber}
          />
          <InvertibleDateTime value={data?.createdAt} />
        </div>
        {(showExpire || showRefund || showReinitiate) && (
          <div className="flex flex-row flex-wrap items-center gap-2 justify-between">
            {showExpire && (
              <PrimeDetailsActionButton
                label="Expire"
                icon="timer_off"
                tone="danger"
                skipBusy
                onClick={() => setExpireOpen(true)}
              />
            )}
            {showRefund && (
              <PrimeDetailsActionButton
                label="Refund"
                icon="undo"
                tone="danger"
                skipBusy
                onClick={() => setRefundOpen(true)}
              />
            )}
            {showReinitiate && (
              <PrimeDetailsActionButton
                label="Re-initiate"
                icon="refresh"
                tone="warning"
                skipBusy
                onClick={() => setReinitiateOpen(true)}
              />
            )}
          </div>
        )}
        <div className="flex flex-row flex-wrap items-center gap-2">
          <PrimeDetailsActionButton
            label="Send callback"
            icon="send"
            onClick={handleSendCallback}
            loading={isButtonLoading}
          />
          <div className="ml-auto">
            <PrimeDetailsActionButton
              label="Timeline"
              icon={onClickStatus ? "keyboard_arrow_up" : "timeline"}
              onClick={() => setOnClickStatus((prev) => !prev)}
              skipBusy
            />
          </div>
        </div>
        {onClickStatus && <EventTimeline events={eventDetails} />}
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Settlement Details:
        </p>
        <LabelRow
          label="Network"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              {settlementDetails?.networkImage && (
                <img
                  src={settlementDetails.networkImage}
                  height={12}
                  width={12}
                  alt=""
                />
              )}
              <span className="text-text-base text-[12px] font-normal">
                {settlementDetails?.network ?? "- -"}
              </span>
            </div>
          }
        />
        <LabelRow
          label="Transfer Type"
          valueComp={
            <span
              className={`flex text-[10px] h-[20px] px-[8px] font-bold text-center items-center rounded-lg ${transferPill.className}`}
            >
              {transferPill.text}
            </span>
          }
        />
        <LabelRow
          label="Wallet Address"
          value={settlementDetails?.walletAddress ?? ""}
          valueComp={
            <TruncatedCopyValue
              value={settlementDetails?.walletAddress}
              copyMessage="Wallet address copied to clipboard"
            />
          }
        />
        <LabelRow
          label="Settlement Type"
          valueComp={
            <span
              className={`flex text-[10px] h-[20px] px-[8px] font-bold text-center items-center rounded-lg ${settlementPill.className}`}
            >
              {settlementPill.text}
            </span>
          }
        />
        <LabelRow
          label="Transaction Hash"
          valueComp={
            settlementHash ? (
              <div className="flex flex-row items-center gap-[4px]">
                <TruncatedCopyValue
                  value={settlementHash}
                  copyMessage="Transaction hash copied to clipboard"
                />
                {settlementNetwork && (
                  <RedirectButton
                    transactionHash={settlementHash}
                    network={settlementNetwork}
                    fillColor=""
                  />
                )}
              </div>
            ) : (
              <span className="text-text-base text-[12px] font-normal">
                - -
              </span>
            )
          }
        />
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Trade Details:
        </p>
        <LabelRow
          label="Token | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.cryptoCurrencySymbol}
              </span>
              <span className="text-text-base text-[12px] font-normal">|</span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.fiatCurrencySymbol}
              </span>
            </div>
          }
        />
        <LabelRow
          label="Amount in VDA | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.cryptoAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">|</span>
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.fiatAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
            </div>
          }
        />
        <LabelRow
          label="Rate"
          valueComp={
            <InvertibleRate
              cryptoToFiatRate={
                data?.rate != null ? Number(data.rate) : undefined
              }
              cryptoImage={data?.cryptoCurrencyImage}
              cryptoSymbol={data?.cryptoCurrencySymbol}
              fiatImage={data?.fiatCurrencyImage}
              fiatSymbol={data?.fiatCurrencySymbol}
            />
          }
        />
        <LabelRow
          label="Fees"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.fees || 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
            </div>
          }
        />
        <LabelRow
          label="MDR"
          value={`${Number(data?.MDR || 0).toFixed(2)} %`}
        />
        <LabelRow
          label="Amount to be released"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.toReleaseAmount || 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
            </div>
          }
        />
      </div>
      <ConfirmCadExpirePanel
        open={expireOpen}
        instanceId={instanceId}
        onClose={() => setExpireOpen(false)}
        onSuccess={() => setExpireRequested(true)}
      />
      <ConfirmRefundPanel
        open={refundOpen}
        instanceId={instanceId}
        market="canada"
        onClose={() => setRefundOpen(false)}
        onSuccess={handleEventChange}
      />
      <ConfirmReinitiatePanel
        open={reinitiateOpen}
        instanceId={instanceId}
        market="canada"
        onClose={() => setReinitiateOpen(false)}
        onSuccess={handleEventChange}
      />
    </div>
  );
};

export default PrimeCanadaOrderFlowDetails;
